import { useEffect } from "react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { ContactLink, AssessmentButton } from "@/components/Ctas";

const CHALLENGES = [
  "Programs growing faster than the processes supporting them",
  "Too much knowledge living in individual people",
  "Teams balancing competing priorities",
  "Strategic plans that are difficult to translate into action",
  "Limited organizational capacity",
  "Processes that need to become clearer and more consistent",
  "Leadership and delegation challenges",
  "Evaluation and learning that isn't consistently translated into improvement",
  "Uncertainty about where technology or AI could realistically help",
];

const AREAS = [
  {
    number: "01",
    title: "Clarify the Mission",
    description:
      "Connect mission, priorities, programs and outcomes so the organization knows where to focus and what matters most.",
    services: ["Program strategy", "Strategic planning", "Program design", "Prioritization", "Outcome mapping", "Opportunity mapping"],
    testid: "nonprofits-area-mission",
  },
  {
    number: "02",
    title: "Strengthen the Organization",
    description:
      "Build stronger programs, processes, leadership and organizational capacity to support the mission.",
    services: ["Program management", "Process improvement", "Operating systems", "Workflow design", "Leadership & team development", "Capacity building", "Continuous improvement"],
    testid: "nonprofits-area-organization",
  },
  {
    number: "03",
    title: "Add Intelligence",
    description:
      "Use practical AI and automation where they can reduce repetitive work, improve workflows and strengthen organizational capacity.",
    services: ["AI opportunity mapping", "AI readiness", "Workflow automation", "AI-powered operations", "Practical AI implementation"],
    testid: "nonprofits-area-intelligence",
  },
];

const ENGAGEMENTS = [
  {
    name: "Program Strategy & Design",
    description:
      "For organizations that need to clarify program direction, goals, priorities and outcomes.",
    testid: "engagement-program-strategy",
  },
  {
    name: "Process & Capacity Building",
    description:
      "For organizations that need clearer processes, stronger delegation and more consistent ways of working.",
    testid: "engagement-capacity",
  },
  {
    name: "Workshops & Facilitation",
    description:
      "Practical, facilitated sessions that build team capability around a known challenge.",
    testid: "engagement-workshops",
  },
  {
    name: "Systems & Practical AI",
    description:
      "For organizations ready to strengthen systems, reduce manual work and explore practical AI opportunities.",
    testid: "engagement-systems-ai",
  },
];

const METHOD = ["ASSESS", "CLARIFY", "PRIORITIZE", "BUILD", "LEARN", "ADAPT"];

export default function Nonprofits() {
  useEffect(() => {
    document.title = "For Nonprofits — KP Strategy";
    return () => {
      document.title =
        "KP Strategy — Design your business like a product. Run it like a system. Scale it with AI.";
    };
  }, []);

  return (
    <main data-testid="nonprofits-page">
      {/* Hero */}
      <section className="pt-[72px]" data-testid="nonprofits-hero">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 sm:pt-24 lg:px-8 lg:pb-20 lg:pt-28">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-xs font-medium uppercase tracking-[0.3em] text-amber">
              <span className="inline-block h-px w-10 bg-amber" aria-hidden="true" />
              For Nonprofits
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-7 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl md:text-6xl">
              Strengthen your programs. Build better processes. Grow your
              capacity.
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-body sm:text-lg">
              KP Strategy helps nonprofits and community organizations
              strengthen programs, improve ways of working, develop
              organizational capacity and turn strategy into practical action.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-9 flex flex-wrap items-center gap-6">
              <AssessmentButton testid="nonprofits-cta-assessment" dark={false} />
              <ContactLink testid="nonprofits-cta-get-in-touch" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Challenges */}
      <section className="border-t border-line py-20 sm:py-24" data-testid="nonprofits-challenges">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="01 — The Reality"
            title="Your mission shouldn't have to depend on complexity."
            testid="nonprofits-challenges-head"
          />
          <ul className="mt-12 grid max-w-4xl grid-cols-1 gap-x-12 sm:grid-cols-2" data-testid="nonprofits-challenges-list">
            {CHALLENGES.map((c, i) => (
              <Reveal key={c} delay={i * 0.05}>
                <li className="flex items-baseline gap-3 border-b border-line py-4 text-base leading-relaxed text-body">
                  <span className="text-amber" aria-hidden="true">✦</span>
                  {c}
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* How KP Strategy helps */}
      <section className="border-t border-line bg-parch py-20 sm:py-28" data-testid="nonprofits-areas">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="02 — How I Help"
            title="Practical support for the organization behind the mission."
            testid="nonprofits-areas-head"
          />
          <div className="mt-14 border-t border-ink/10">
            {AREAS.map((a, i) => (
              <Reveal key={a.number} delay={i * 0.08}>
                <div
                  data-testid={a.testid}
                  className="grid grid-cols-1 gap-6 border-b border-ink/10 py-10 lg:grid-cols-12 lg:gap-10"
                >
                  <div className="lg:col-span-4">
                    <p className="font-serif text-5xl font-light text-amber/90">
                      {a.number}
                    </p>
                    <h3 className="mt-4 font-serif text-2xl font-normal text-ink sm:text-3xl">
                      {a.title}
                    </h3>
                    <p className="mt-3 max-w-sm text-base leading-relaxed text-body">
                      {a.description}
                    </p>
                  </div>
                  <ul className="flex flex-wrap content-start gap-2 lg:col-span-8 lg:pt-2">
                    {a.services.map((s) => (
                      <li
                        key={s}
                        className="border border-line bg-paper px-3.5 py-2 font-mono text-[11px] uppercase tracking-wider text-body transition-colors duration-300 hover:border-amber/60 hover:text-ink"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How we work */}
      <section className="border-t border-line py-20 sm:py-24" data-testid="nonprofits-methodology">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="03 — The Approach"
            title="Build the capacity to move the mission forward."
            copy="A shared methodology, adapted to your organization's capacity and context."
            testid="nonprofits-methodology-head"
          />
          <Reveal delay={0.15}>
            <ol className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-0" data-testid="nonprofits-methodology-strip">
              {METHOD.map((step, i) => (
                <li key={step} className="flex items-center">
                  <span className="flex items-baseline gap-2">
                    <span className="font-mono text-[11px] text-amber">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-mono text-sm font-medium uppercase tracking-[0.15em] text-ink">
                      {step}
                    </span>
                  </span>
                  {i < METHOD.length - 1 && (
                    <span className="hidden px-4 text-ink/30 sm:inline" aria-hidden="true">
                      →
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      {/* Working Together */}
      <section className="border-t border-line py-20 sm:py-28" data-testid="nonprofits-engagements">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="04 — Working Together"
            title="What working together can look like"
            copy="Flexible formats, shaped around your organization's needs — not rigid packages."
            testid="nonprofits-engagements-head"
          />
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
            {ENGAGEMENTS.map((e, i) => (
              <Reveal key={e.name} delay={i * 0.08} className="h-full">
                <div
                  data-testid={e.testid}
                  className="group h-full border border-line bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-amber/60 sm:p-10"
                >
                  <h3 className="font-serif text-2xl font-normal text-ink">
                    {e.name}
                  </h3>
                  <span className="mt-5 block h-px w-10 bg-amber transition-all duration-500 group-hover:w-full" />
                  <p className="mt-5 text-base leading-relaxed text-body">
                    {e.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-ink py-24 text-paper sm:py-32" data-testid="nonprofits-final-cta">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber">
              05 — Next Step
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl md:text-5xl">
              Strengthen the organization behind the mission.
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-paper/70 sm:text-lg">
              Let&apos;s identify what needs to work better and build a
              practical path forward.
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-7">
              <AssessmentButton testid="nonprofits-final-cta-assessment" dark />
              <ContactLink testid="nonprofits-final-cta-get-in-touch" dark />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
